// Copyright © 2023 David Caldwell <david@greenfelt.net>

var geometry = require('./geometry'); var { point_scale, point_sum, polar_to_cartesian, point_difference, point_dot, point_cross, point_norm,
                                            point_squared_distance, point_distance, point_det, signed_distance_to_line, distance_to_line,
                                            distance_to_line_segment, lines_intersect, percentage_along_line } = geometry;

var prnt = require('./print'), print=prnt.print, printf=prnt.printf, sprintf=prnt.sprintf;

class Polygon {
    constructor(vertices) {
        let start, end;
        for (let [i,v] of vertices.entries()) {
            let poly_node = { x: v[0], y: v[1],
                              prev: end,
                              done: {},
                              name: `${i}`,
                            };
            if (end) end.next = poly_node;
            end = poly_node;
            if (!start) start = poly_node;
            if (isNaN(poly_node.x) || isNaN(poly_node.y)) throw "Got me some NaNs";
        }
        start.prev = end; // make it loop!
        end.next = start;

        this.start = start;
        this.generation = 0;
    }

    insert(point, after) {
        // s.next -> sn
        // s      <- sn.prev
        point.next = after.next;
        point.prev = after;
        point.next.prev = point;
        after.next = point;
        this.generation++;
    }

    [window.Symbol.iterator]() {
        let current = this.start, last = this.start.prev;
        let gen = this.generation;
        return {
            next: () => {
                if (current == undefined) return { done: true, value: undefined }
                let value = current;
                do {
                    current = current == last ? undefined : current.next;
                } while (current && gen < current.generation)
                return { done: false, value: [value, value.next] }
            }
        }
    }
}

function split_polygon(vertices, line) {
    let poly = new Polygon(vertices);

    split_edges(poly, line);
    let no_cuts = !choose_sides(poly, line);

    let sorted_intersects = [...poly].map(e => e[0]).filter(e => 'along_cutline' in e).sort((a,b) => a.along_cutline - b.along_cutline);

    sorted_intersects.forEach((int, i) => {
        let odd = (i+1)&1;
        if (odd) int.pair = sorted_intersects[i+1]; // explicitly assymmetric
        else     int.pair = sorted_intersects[i-1];
    });


    let debug = false; //!window.once;
    if (debug && !window.once) {
        debug = new PolygonDebugCanvas(poly, line);
        print(debug.el);
        print("\n===== Intersected Poly ===== ");
        printf("line=[%.2f,%.2f]->[%.2f,%.2f]\n", line[0].x, line[0].y, line[1].x, line[1].y);
        for (let [v,_v] of poly) {
            printf("%s: [%6.2f,%6.2f] side %s%s\n", v.name, v.x, v.y, v.side || '|',
                   !v.pair ? "" : sprintf(", pair %s %s. Side A=>%s, Side B=>%s", "=>", v.pair.name, v.dir.A, v.dir.B));
        }
        window.once = true;
    }

    if (sorted_intersects.length == 0 || no_cuts /*no buts, no coconuts*/) {
        // Either the line never intersected the polygon or the polygon was completely degenerate and every point was on the line.
        // Either way, just return the original polygon unmolested.
        return [[...poly].map(([v,_]) => [v.x, v.y])];
    }

    let out = [];

    let follow_cuts = (start, side) => {
        let poly = [];

        let current = start;
        let exiting = true;
        let dir = current.dir[side];

        if (debug) printf("===== Follow Cuts ===== start=%s, side=%s, direction=%s\n", start.name, side, dir);

        do {
            if (debug) printf(" [%s] %s: [%6.2f,%6.2f]%s\n", dir, current.name, current.x, current.y,
                              !current.pair ? "" : sprintf(" pair %s %s, Side %s => %s", "=>", current.pair.name, side, current.dir[side]));
            if (current.done[side]) throw(`already hit ${current.name} for ${dir}!`) // probably bad

            poly.push([current.x, current.y]);

            if (current.pair)
                current.done[side] = true;

            if (exiting = !exiting && current.pair) {
                current = current.pair; // Bridge the gap across the cut
                dir = current.dir[side];
            } else
                current = current[dir];


        } while (current != start)
        return poly;
    };

    for (let i=0; i<sorted_intersects.length; i+=2) {
        if (!sorted_intersects[i].done.A) out.push(follow_cuts(sorted_intersects[i], "A")); // One "half"
        if (!sorted_intersects[i].done.B) out.push(follow_cuts(sorted_intersects[i], "B")); // The other "half"
    }

    if (debug) debug.add_outs(out);

    return out;
}

function split_edges(poly, line) {
    let name = "A".charCodeAt(0);
    for (let e of poly) {
        let alpha = lines_intersect(...line, ...e);
        if (!alpha) continue;
        let int = percentage_along_line(...e, alpha[1]);
        poly.insert(({ ...int, name: String.fromCharCode(name++), done: {}, along_cutline: alpha[0] }), e[0]);
    }
}

function choose_sides(poly, line) {
    for (let [v, _] of poly) {
        let d;
        if ('along_cutline' in v) {
            let v1 = v;
            do {
                v1 = v1.next;
                d = point_det(v1, line[0], line[1]);
            } while (d == 0 && v != v1);
            if (d == 0) return false; // _every_ point was on the line. Nothing to split.
            let side = d>0 ? "A" : "B";
            v.dir = { A: side == "A" ? "next" : "prev",
                      B: side == "B" ? "next" : "prev",};
        } else { // We don't need the else clause, it's just for debugging.
            d = point_det(v, line[0], line[1]);
            let side = d>0 ? "A" :
                d<0 ? "B"
                : "|";
            v.side = side;
        }
    }
    return true;
}

const RED=0, GREEN=120, BLUE=240;
class PolygonDebugCanvas {
    constructor(poly, line) {
        this.line = line;
        let bounds = [...poly].reduce((memo, [pt,_]) => ({ x1: Math.min(memo.x1, pt.x), y1: Math.min(memo.y1, pt.y),
                                                               x2: Math.max(memo.x2, pt.x), y2: Math.max(memo.y2, pt.y), }),
                                              { x1: poly.start.x, y1: poly.start.y,
                                                x2: poly.start.x, y2: poly.start.y });
        this.size = { width: bounds.x2 - bounds.x1,
                      height: bounds.y2 - bounds.y1 };
        this.zoom = 6;
        this.hspace = 3;
        let doc = require('./dom').doc;
        this.el = doc(["canvas", { style: { width: `${this.size.width*this.zoom*this.hspace}px`, height: `${this.size.height*this.zoom}px`} }]);
        doc.id("debug").appendChild(this.el); // I think I should be able to comment this out, but when I do I get a few weird semi transparent white rectangles. It's hard to debug because it's offscreen :-)
        this.el.width = this.size.width*this.zoom*this.hspace;
        this.el.height = this.size.height*this.zoom;
        this.ctx = this.el.getContext("2d");
        this.ctx.fillStyle = "black";
        this.ctx.fillRect(0,0, this.el.width, this.el.height);
        this.ctx.scale(this.zoom, this.zoom);
        this.ctx.translate(this.size.width*this.hspace/2, this.size.height/2);
        this.ctx.scale(.93, .93);
        this.ctx.translate(-this.size.width*this.hspace/2, -this.size.height/2);
        this.ctx.translate(-bounds.x1, -bounds.y1);
        this.ctx.lineWidth = 3/this.zoom;

        this.style = {
            subj:     `hsl(${GREEN},100%,35%)`,
            subj_pt:  `hsl(${GREEN},100%,25%)`,
            subj_fill:`hsl(${GREEN}, 80%,15%)`,
            inter_pt: `hsl(${RED}, 100%, 25%)`,
            clip: `hsl(${BLUE-40},100%,45%)`,
            clip_pt: `hsl(${BLUE-40},100%,35%)`,
            line:    'hsla(0, 0%, 50%, 50%)',
            line_pt: 'hsla(0, 0%, 50%, 60%)',
        }
        this.draw(poly, true, this.style.subj_fill, this.style.subj, this.style.subj_pt, this.style.inter_pt);

        this.draw_line();
    }

    draw(poly, labels, fill_style, line_style, point_style, intersection_style) {
        this.ctx.beginPath();
        for (let [v,_] of poly)
            this.ctx.lineTo(v.x, v.y);
        this.ctx.closePath();
        this.ctx.fillStyle = fill_style;
        this.ctx.fill();
        this.ctx.strokeStyle = line_style;
        this.ctx.stroke();

        for (let [v,_] of poly) {
            this.ctx.fillStyle = 'along_cutline' in v ? intersection_style : point_style;
            this.ctx.fillRect(v.x-1, v.y-1, 2, 2);
        }
        for (let [v,_] of poly) {
            this.ctx.font = `${12/this.zoom}px sans-serif`;
            this.ctx.textAlign = "center";
            this.ctx.textBaseline = "middle";
            this.ctx.fillStyle = "white";
            this.ctx.fillText(`${v.name}`, v.x, v.y+.5/*1/2 of the -1 for the point rect*/);
            if (labels && v.neighbor) {
                this.ctx.fillStyle = "white";
                this.ctx.fillText(`${v.entry_exit == 'EXIT' ? "<-" : "->"} ${v.neighbor.name}`, v.x, v.y+14/this.zoom);
            }
        }
    }

    draw_line() {
        this.ctx.moveTo(this.line[0].x, this.line[0].y);
        this.ctx.lineTo(this.line[1].x, this.line[1].y);
        this.ctx.strokeStyle = this.style.line;
        this.ctx.stroke();
        this.ctx.fillStyle = this.style.line_pt;
        this.ctx.fillRect(this.line[0].x-1, this.line[0].y-1, 2, 2);
        this.ctx.fillRect(this.line[1].x-1, this.line[1].y-1, 2, 2);
    }

    add_outs(out) {
        this.ctx.translate(this.size.width, 0);

        let d = point_difference(this.line[0], this.line[1]);
        let normal = { x:-d.y, y:d.x };
        let normal_unit = point_scale(1/point_distance({x:0,y:0}, normal), normal);

        for (let [i, o] of out.entries())
            this.draw(new Polygon(o), true,
                      `hsl(${BLUE-40 + i*30},  80%, 20%)`,
                      `hsl(${BLUE-40 + i*30}, 100%, 35%)`,
                      `hsl(${BLUE-40 + i*30}, 100%, 25%)`);
    }
}

module.exports = {
    Polygon:       Polygon,
    split_polygon: split_polygon,
}
// Copyright © 2023 David Caldwell <david@greenfelt.net>

var geometry = require('./geometry'); var { point_scale, point_sum, polar_to_cartesian, point_difference, point_dot, point_cross, point_norm,
                                            point_squared_distance, point_distance, point_det, signed_distance_to_line, distance_to_line,
                                            distance_to_line_segment, lines_intersect, percentage_along_line } = geometry;

var prnt = require('./print'), print=prnt.print, printf=prnt.printf, sprintf=prnt.sprintf;

class Polygon {
    constructor(vertices) {
        let start, end;
        for (let [i,v] of vertices.entries()) {
            let poly_node = { x: v[0], y: v[1],
                              prev: end,
                              done: {},
                              name: `${i}`,
                            };
            if (end) end.next = poly_node;
            end = poly_node;
            if (!start) start = poly_node;
            if (isNaN(poly_node.x) || isNaN(poly_node.y)) throw "Got me some NaNs";
        }
        start.prev = end; // make it loop!
        end.next = start;

        this.start = start;
        this.generation = 0;
    }

    insert(point, after) {
        // s.next -> sn
        // s      <- sn.prev
        point.next = after.next;
        point.prev = after;
        point.next.prev = point;
        after.next = point;
        this.generation++;
    }

    [window.Symbol.iterator]() {
        let current = this.start, last = this.start.prev;
        let gen = this.generation;
        return {
            next: () => {
                if (current == undefined) return { done: true, value: undefined }
                let value = current;
                do {
                    current = current == last ? undefined : current.next;
                } while (current && gen < current.generation)
                return { done: false, value: [value, value.next] }
            }
        }
    }
}

function split_polygon(vertices, line) {
    let poly = new Polygon(vertices);

    split_edges(poly, line);
    let no_cuts = !choose_sides(poly, line);

    let sorted_intersects = [...poly].map(e => e[0]).filter(e => 'along_cutline' in e).sort((a,b) => a.along_cutline - b.along_cutline);

    sorted_intersects.forEach((int, i) => {
        let odd = (i+1)&1;
        if (odd) int.pair = sorted_intersects[i+1]; // explicitly assymmetric
        else     int.pair = sorted_intersects[i-1];
    });


    let debug = false; //!window.once;
    if (debug && !window.once) {
        debug = new PolygonDebugCanvas(poly, line);
        print(debug.el);
        print("\n===== Intersected Poly ===== ");
        printf("line=[%.2f,%.2f]->[%.2f,%.2f]\n", line[0].x, line[0].y, line[1].x, line[1].y);
        for (let [v,_v] of poly) {
            printf("%s: [%6.2f,%6.2f] side %s%s\n", v.name, v.x, v.y, v.side || '|',
                   !v.pair ? "" : sprintf(", pair %s %s. Side A=>%s, Side B=>%s", "=>", v.pair.name, v.dir.A, v.dir.B));
        }
        window.once = true;
    }

    if (sorted_intersects.length == 0 || no_cuts /*no buts, no coconuts*/) {
        // Either the line never intersected the polygon or the polygon was completely degenerate and every point was on the line.
        // Either way, just return the original polygon unmolested.
        return [[...poly].map(([v,_]) => [v.x, v.y])];
    }

    let out = [];

    let follow_cuts = (start, side) => {
        let poly = [];

        let current = start;
        let exiting = true;
        let dir = current.dir[side];

        if (debug) printf("===== Follow Cuts ===== start=%s, side=%s, direction=%s\n", start.name, side, dir);

        do {
            if (debug) printf(" [%s] %s: [%6.2f,%6.2f]%s\n", dir, current.name, current.x, current.y,
                              !current.pair ? "" : sprintf(" pair %s %s, Side %s => %s", "=>", current.pair.name, side, current.dir[side]));
            if (current.done[side]) throw(`already hit ${current.name} for ${dir}!`) // probably bad

            poly.push([current.x, current.y]);

            if (current.pair)
                current.done[side] = true;

            if (exiting = !exiting && current.pair) {
                current = current.pair; // Bridge the gap across the cut
                dir = current.dir[side];
            } else
                current = current[dir];


        } while (current != start)
        return poly;
    };

    for (let i=0; i<sorted_intersects.length; i+=2) {
        if (!sorted_intersects[i].done.A) out.push(follow_cuts(sorted_intersects[i], "A")); // One "half"
        if (!sorted_intersects[i].done.B) out.push(follow_cuts(sorted_intersects[i], "B")); // The other "half"
    }

    if (debug) debug.add_outs(out);

    return out;
}

function split_edges(poly, line) {
    let name = "A".charCodeAt(0);
    for (let e of poly) {
        let alpha = lines_intersect(...line, ...e);
        if (!alpha) continue;
        let int = percentage_along_line(...e, alpha[1]);
        poly.insert(({ ...int, name: String.fromCharCode(name++), done: {}, along_cutline: alpha[0] }), e[0]);
    }
}

function choose_sides(poly, line) {
    for (let [v, _] of poly) {
        let d;
        if ('along_cutline' in v) {
            let v1 = v;
            do {
                v1 = v1.next;
                d = point_det(v1, line[0], line[1]);
            } while (d == 0 && v != v1);
            if (d == 0) return false; // _every_ point was on the line. Nothing to split.
            let side = d>0 ? "A" : "B";
            v.dir = { A: side == "A" ? "next" : "prev",
                      B: side == "B" ? "next" : "prev",};
        } else { // We don't need the else clause, it's just for debugging.
            d = point_det(v, line[0], line[1]);
            let side = d>0 ? "A" :
                d<0 ? "B"
                : "|";
            v.side = side;
        }
    }
    return true;
}

const RED=0, GREEN=120, BLUE=240;
class PolygonDebugCanvas {
    constructor(poly, line) {
        this.line = line;
        let bounds = [...poly].reduce((memo, [pt,_]) => ({ x1: Math.min(memo.x1, pt.x), y1: Math.min(memo.y1, pt.y),
                                                               x2: Math.max(memo.x2, pt.x), y2: Math.max(memo.y2, pt.y), }),
                                              { x1: poly.start.x, y1: poly.start.y,
                                                x2: poly.start.x, y2: poly.start.y });
        this.size = { width: bounds.x2 - bounds.x1,
                      height: bounds.y2 - bounds.y1 };
        this.zoom = 6;
        this.hspace = 3;
        let doc = require('./dom').doc;
        this.el = doc(["canvas", { style: { width: `${this.size.width*this.zoom*this.hspace}px`, height: `${this.size.height*this.zoom}px`} }]);
        doc.id("debug").appendChild(this.el); // I think I should be able to comment this out, but when I do I get a few weird semi transparent white rectangles. It's hard to debug because it's offscreen :-)
        this.el.width = this.size.width*this.zoom*this.hspace;
        this.el.height = this.size.height*this.zoom;
        this.ctx = this.el.getContext("2d");
        this.ctx.fillStyle = "black";
        this.ctx.fillRect(0,0, this.el.width, this.el.height);
        this.ctx.scale(this.zoom, this.zoom);
        this.ctx.translate(this.size.width*this.hspace/2, this.size.height/2);
        this.ctx.scale(.93, .93);
        this.ctx.translate(-this.size.width*this.hspace/2, -this.size.height/2);
        this.ctx.translate(-bounds.x1, -bounds.y1);
        this.ctx.lineWidth = 3/this.zoom;

        this.style = {
            subj:     `hsl(${GREEN},100%,35%)`,
            subj_pt:  `hsl(${GREEN},100%,25%)`,
            subj_fill:`hsl(${GREEN}, 80%,15%)`,
            inter_pt: `hsl(${RED}, 100%, 25%)`,
            clip: `hsl(${BLUE-40},100%,45%)`,
            clip_pt: `hsl(${BLUE-40},100%,35%)`,
            line:    'hsla(0, 0%, 50%, 50%)',
            line_pt: 'hsla(0, 0%, 50%, 60%)',
        }
        this.draw(poly, true, this.style.subj_fill, this.style.subj, this.style.subj_pt, this.style.inter_pt);

        this.draw_line();
    }

    draw(poly, labels, fill_style, line_style, point_style, intersection_style) {
        this.ctx.beginPath();
        for (let [v,_] of poly)
            this.ctx.lineTo(v.x, v.y);
        this.ctx.closePath();
        this.ctx.fillStyle = fill_style;
        this.ctx.fill();
        this.ctx.strokeStyle = line_style;
        this.ctx.stroke();

        for (let [v,_] of poly) {
            this.ctx.fillStyle = 'along_cutline' in v ? intersection_style : point_style;
            this.ctx.fillRect(v.x-1, v.y-1, 2, 2);
        }
        for (let [v,_] of poly) {
            this.ctx.font = `${12/this.zoom}px sans-serif`;
            this.ctx.textAlign = "center";
            this.ctx.textBaseline = "middle";
            this.ctx.fillStyle = "white";
            this.ctx.fillText(`${v.name}`, v.x, v.y+.5/*1/2 of the -1 for the point rect*/);
            if (labels && v.neighbor) {
                this.ctx.fillStyle = "white";
                this.ctx.fillText(`${v.entry_exit == 'EXIT' ? "<-" : "->"} ${v.neighbor.name}`, v.x, v.y+14/this.zoom);
            }
        }
    }

    draw_line() {
        this.ctx.moveTo(this.line[0].x, this.line[0].y);
        this.ctx.lineTo(this.line[1].x, this.line[1].y);
        this.ctx.strokeStyle = this.style.line;
        this.ctx.stroke();
        this.ctx.fillStyle = this.style.line_pt;
        this.ctx.fillRect(this.line[0].x-1, this.line[0].y-1, 2, 2);
        this.ctx.fillRect(this.line[1].x-1, this.line[1].y-1, 2, 2);
    }

    add_outs(out) {
        this.ctx.translate(this.size.width, 0);

        let d = point_difference(this.line[0], this.line[1]);
        let normal = { x:-d.y, y:d.x };
        let normal_unit = point_scale(1/point_distance({x:0,y:0}, normal), normal);

        for (let [i, o] of out.entries())
            this.draw(new Polygon(o), true,
                      `hsl(${BLUE-40 + i*30},  80%, 20%)`,
                      `hsl(${BLUE-40 + i*30}, 100%, 35%)`,
                      `hsl(${BLUE-40 + i*30}, 100%, 25%)`);
    }
}

module.exports = {
    Polygon:       Polygon,
    split_polygon: split_polygon,
}
